/**
 * Platform admin card: Claude API gateway (CLAUDE.md §3.6).
 * Wires GET/PUT /api/admin/claude-gateway: month-to-date Anthropic spend, the
 * monthly cap, the force-OpenRouter kill switch, and spend + prompt-cache hit
 * rate per workload. Per-workload model choice (and one-click rollback to the
 * OpenRouter fallback) lives in WorkloadModelRoutingPanel.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { TextInput, View } from 'react-native';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { AdminSectionCard, ADMIN_COLORS } from '@/components/admin/AdminUI';
import { apiRequest } from '../../services/apiClient';
import { useTranslation } from '../../hooks/useTranslation';
import { useToast } from '../../context/ToastContext';
import {
  budgetTone,
  claudeGatewayErrorKey,
  formatHitRate,
  formatUsd,
  gatewayStateKey,
  parseMonthlyCap,
  type ClaudeGatewayStatus,
  type ClaudeGatewayUpdate,
} from '../../utils/claudeGateway';

const TONE_COLORS = {
  ok: { bg: ADMIN_COLORS.successFaint, border: ADMIN_COLORS.successBorder, fg: ADMIN_COLORS.successText },
  warning: { bg: ADMIN_COLORS.warningSoft, border: ADMIN_COLORS.warningBorder, fg: ADMIN_COLORS.warningText },
  exhausted: { bg: ADMIN_COLORS.errorSoft, border: ADMIN_COLORS.errorBorder, fg: ADMIN_COLORS.errorText },
} as const;

export default function ClaudeGatewayCard() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [status, setStatus] = useState<ClaudeGatewayStatus | null>(null);
  const [capInput, setCapInput] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await apiRequest<ClaudeGatewayStatus>({ endpoint: '/admin/claude-gateway' });
      setStatus(response);
      setCapInput(String(response.monthly_cap_usd));
    } catch (error: any) {
      showToast(t(claudeGatewayErrorKey(error?.status)), 'error');
    }
  }, [showToast, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (update: ClaudeGatewayUpdate) => {
    setSaving(true);
    try {
      const response = await apiRequest<ClaudeGatewayStatus, ClaudeGatewayUpdate>({
        endpoint: '/admin/claude-gateway',
        method: 'PUT',
        data: update,
      });
      setStatus(response);
      setCapInput(String(response.monthly_cap_usd));
      showToast(t('platformAdmin.claudeGateway.saved'), 'success');
    } catch (error: any) {
      showToast(t(claudeGatewayErrorKey(error?.status)), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCap = () => {
    const parsed = parseMonthlyCap(capInput);
    if ('errorKey' in parsed) {
      showToast(t(parsed.errorKey), 'error');
      return;
    }
    void save({ monthly_cap_usd: parsed.value });
  };

  const tone = status ? TONE_COLORS[budgetTone(status)] : TONE_COLORS.ok;

  return (
    <AdminSectionCard
      icon="savings"
      title={t('platformAdmin.claudeGateway.title')}
      subtitle={t('platformAdmin.claudeGateway.description')}
    >
      {status ? (
        <View style={{ gap: 14 }} testID="claude-gateway-card">
          <View
            style={{
              borderWidth: 1,
              borderColor: tone.border,
              backgroundColor: tone.bg,
              borderRadius: 12,
              padding: 12,
              gap: 4,
            }}
          >
            <Text style={{ color: tone.fg, fontWeight: '700' }} testID="claude-gateway-spend">
              {t('platformAdmin.claudeGateway.spent')}: {formatUsd(status.spent_usd)} / {formatUsd(status.monthly_cap_usd)} (
              {Math.round(status.percent_used)}%) · {status.month}
            </Text>
            <Text style={{ color: tone.fg }} testID="claude-gateway-state">
              {t(gatewayStateKey(status))}
            </Text>
          </View>

          <View style={{ flexDirection: 'row', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Text style={{ color: ADMIN_COLORS.text.muted, fontWeight: '700' }}>
              {t('platformAdmin.claudeGateway.cap')}
            </Text>
            <TextInput
              value={capInput}
              onChangeText={setCapInput}
              placeholder={t('platformAdmin.claudeGateway.capPlaceholder')}
              keyboardType="decimal-pad"
              accessibilityLabel={t('platformAdmin.claudeGateway.cap')}
              testID="claude-gateway-cap"
              style={{
                minWidth: 120,
                borderWidth: 1,
                borderColor: ADMIN_COLORS.border,
                borderRadius: 10,
                paddingHorizontal: 12,
                paddingVertical: 8,
                fontSize: 16,
                color: ADMIN_COLORS.text.primary,
              }}
            />
            <Button onPress={handleSaveCap} disabled={saving}>
              <Text className="text-white font-medium">{t('platformAdmin.claudeGateway.saveCap')}</Text>
            </Button>
            <Button
              variant="outline"
              onPress={() => void save({ force_openrouter: !status.force_openrouter })}
              disabled={saving}
              testID="claude-gateway-kill-switch"
            >
              <Text className="font-medium">
                {status.force_openrouter
                  ? t('platformAdmin.claudeGateway.resumeClaude')
                  : t('platformAdmin.claudeGateway.forceOpenRouter')}
              </Text>
            </Button>
          </View>

          <View style={{ gap: 6 }}>
            <Text style={{ color: ADMIN_COLORS.text.primary, fontWeight: '700' }}>
              {t('platformAdmin.claudeGateway.byWorkload')}
            </Text>
            {status.by_workload.length === 0 ? (
              <Text style={{ color: ADMIN_COLORS.text.muted }}>{t('platformAdmin.claudeGateway.noSpendYet')}</Text>
            ) : (
              status.by_workload.map((row) => (
                <View
                  key={row.workload_key}
                  style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 12 }}
                >
                  <Text style={{ color: ADMIN_COLORS.text.primary, fontFamily: 'monospace', flex: 1 }}>
                    {row.workload_key}
                  </Text>
                  <Text style={{ color: ADMIN_COLORS.text.muted }}>
                    {formatUsd(row.spent_usd)} · {row.calls} {t('platformAdmin.claudeGateway.calls')} ·{' '}
                    {formatHitRate(row.cache_hit_rate)} {t('platformAdmin.claudeGateway.cacheHitRate')}
                  </Text>
                </View>
              ))
            )}
          </View>
        </View>
      ) : (
        <Text style={{ color: ADMIN_COLORS.text.muted }}>{t('common.loading')}</Text>
      )}
    </AdminSectionCard>
  );
}
